// ─────────────────────────────────────────────────────────────────────────────
// Extraction du detail d'erreur (axios, fetch, Error)
// ─────────────────────────────────────────────────────────────────────────────

export const extractErrorDetail = (e) => {
  const d =
    e?.response?.data?.detail ??
    e?.data?.detail ??
    e?.detail ??
    e?.message;

  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.detail || String(x)).join(' ');
  if (d && typeof d === 'object') return d.detail || d.msg || '';
  return typeof d === 'string' ? d : '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Cas structuré : "'{username}' still supervises N user(s)/admin(s): a, b, c."
// ─────────────────────────────────────────────────────────────────────────────

const SUPERVISION_RE =
  /'(.+?)'\s+still supervises\s+(\d+)\s+(admin|user)\(s\)\s*:\s*(.*?)\s*\.?\s*$/i;

const ROLE_LABELS = {
  admin: ['administrateur', 'administrateurs'],
  user:  ['utilisateur',    'utilisateurs'],
};

const STATUS_LABELS_FR = { active: 'actif', inactive: 'inactif' };

export const parseSupervisionError = (detail) => {
  if (typeof detail !== 'string') return null;
  const m = detail.match(SUPERVISION_RE);
  if (!m) return null;

  const [, owner, countRaw, role, namesRaw] = m;
  return {
    owner,
    count: Number(countRaw),
    role:  role.toLowerCase(),
    names: namesRaw.split(',').map((s) => s.trim()).filter(Boolean),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Table des patterns : { regex, translate(match) }
// Ordre : du plus spécifique au plus générique.
// ─────────────────────────────────────────────────────────────────────────────

const ERROR_PATTERNS = [

  // ── Authentification ──────────────────────────────────────────────────
  {
    regex: /^Invalid credentials\.?$/i,
    translate: () => 'Identifiants invalides.',
  },
  {
    regex: /^Your account is deactivated\.?$/i,
    translate: () => 'Votre compte est désactivé.',
  },
  {
    regex: /^Could not validate credentials\.?$/i,
    translate: () => 'Impossible de valider les identifiants.',
  },
  {
    regex: /^Email and password are required\.?$/i,
    translate: () => "L'email et le mot de passe sont requis.",
  },
  {
    regex: /^Login already in progress,?\s*please wait\.?$/i,
    translate: () => 'Connexion déjà en cours, veuillez patienter.',
  },
  {
    regex: /^Current password is incorrect\.?$/i,
    translate: () => 'Le mot de passe actuel est incorrect.',
  },

  // ── Utilisateur / accès ───────────────────────────────────────────────
  {
    regex: /^User not found or access denied\.?$/i,
    translate: () => 'Utilisateur introuvable ou accès refusé.',
  },
  {
    regex: /^User not found\.?$/i,
    translate: () => 'Utilisateur introuvable.',
  },
  {
    regex: /^You cannot delete yourself\.?$/i,
    translate: () => 'Vous ne pouvez pas supprimer votre propre compte.',
  },
  {
    regex: /^User is already (active|inactive)\.?$/i,
    translate: (m) => `Cet utilisateur est déjà ${STATUS_LABELS_FR[m[1].toLowerCase()]}.`,
  },
  {
    regex: /^No active users found for given IDs\.?$/i,
    translate: () => 'Aucun utilisateur actif trouvé pour les identifiants fournis.',
  },
  {
    regex: /^You don't have access to user IDs:\s*(.+)$/i,
    translate: (m) => `Vous n'avez pas accès aux utilisateurs suivants : ${m[1].trim()}.`,
  },
  {
    regex: /^Username already taken\.?$/i,
    translate: () => "Ce nom d'utilisateur est déjà pris.",
  },
  {
    regex: /^Email already registered\.?$/i,
    translate: () => 'Cet email est déjà enregistré.',
  },
  {
    regex: /^Invalid r[ôo]le:\s*(.+)$/i,
    translate: (m) => `Rôle invalide : ${m[1].trim()}.`,
  },

  // ── Supervision — changement de rôle / retrait ──────────────────────────
  {
    regex: /'([^']+)'\s+is still supervising users\/admins/i,
    translate: (m) =>
      `« ${m[1]} » supervise encore des utilisateurs/admins. ` +
      `Retirez-le(la) de leur supervision avant de changer son rôle.`,
  },
  {
    regex: /^(A user must have|At least one supervisor is required)/i,
    translate: () => 'Un utilisateur doit avoir au moins un superviseur.',
  },
  {
    regex: /^This endpoint is only for users\.?$/i,
    translate: () => 'Cette action est réservée aux utilisateurs.',
  },
  {
    regex: /^This method is only for regular users\.?$/i,
    translate: () => 'Cette action est réservée aux utilisateurs standards.',
  },
  {
    regex: /^Only admin users can have a supervisor unassigned\.?$/i,
    translate: () => 'Seuls les comptes administrateurs peuvent se voir retirer un superviseur.',
  },
  {
    regex: /^Only admins can have supervisors updated\.?$/i,
    translate: () => 'Seuls les administrateurs peuvent voir leur(s) superviseur(s) modifié(s).',
  },
  {
    regex: /^Only admins can supervise others\.?$/i,
    translate: () => "Seuls les administrateurs peuvent superviser d'autres comptes.",
  },
  {
    regex: /^An admin cannot supervise themselves\.?$/i,
    translate: () => 'Un administrateur ne peut pas se superviser lui-même.',
  },
  {
    regex: /^Supervisor (.+?) must be an admin\.?$/i,
    translate: (m) => `Le superviseur « ${m[1]} » doit être un administrateur.`,
  },
  {
    regex: /^Supervisor (.+?) not found in list\.?$/i,
    translate: (m) => `Superviseur « ${m[1]} » introuvable dans la liste.`,
  },
  {
    regex: /^'([^']+)' is a viewer \(nbr_max=0\) and cannot be an owner supervisor\.?$/i,
    translate: (m) => `« ${m[1]} » est un compte sans quota (viewer) et ne peut pas être superviseur principal.`,
  },
  {
    regex: /^Cycle detected:\s*'([^']+)' is already a descendant of this admin\.?$/i,
    translate: (m) => `Cycle détecté : « ${m[1]} » est déjà un descendant de cet administrateur.`,
  },
  {
    regex: /^Target must be an admin\.?$/i,
    translate: () => 'La cible doit être un administrateur.',
  },

  // ── Quota / création de comptes ──────────────────────────────────────
  {
    regex: /^nbr_max is (only for admin(s)?( users)?|only for admins)\.?$/i,
    translate: () => 'Le nombre maximum de créations concerne uniquement les administrateurs.',
  },
  {
    regex: /^nbr_max is required for (a sub-admin|admins)\.?$/i,
    translate: () => 'Le nombre maximum de créations est requis pour un administrateur.',
  },
  {
    regex: /^Quota info is only for admins\.?$/i,
    translate: () => 'Les informations de quota concernent uniquement les administrateurs.',
  },
  {
    regex: /^'([^']+)' is a viewer and cannot create accounts\.?$/i,
    translate: (m) => `« ${m[1]} » est un compte sans quota (viewer) et ne peut pas créer de comptes.`,
  },
  {
    regex: /'([^']+)'\s+has no quota left\s*\((\d+)\/(\d+)\s*used\)\.?/i,
    translate: (m) => `« ${m[1]} » n'a plus de quota disponible (${m[2]}/${m[3]} utilisés).`,
  },
  {
    // accepte les nombres négatifs (ex: -32 place(s) disponible(s))
    regex: /^'([^']+)' only has (-?\d+) slot\(s\) available\. Cannot assign nbr_max=(-?\d+)\.?$/i,
    translate: (m) =>
      `« ${m[1]} » ne dispose que de ${m[2]} place(s) disponible(s). ` +
      `Impossible d'attribuer un quota de ${m[3]}.`,
  },
  {
    regex: /^Parent '([^']+)' only allows (-?\d+) slot\(s\) for this sub-admin\.?$/i,
    translate: (m) => `Le compte parent « ${m[1]} » n'autorise que ${m[2]} place(s) pour ce sous-administrateur.`,
  },
  {
    regex: /^Cannot set '([^']+)' as viewer:\s*(\d+) slot\(s\) already distributed\.?$/i,
    translate: (m) =>
      `Impossible de transformer « ${m[1]} » en viewer : ${m[2]} place(s) déjà distribuée(s).`,
  },
  {
    regex: /^'([^']+)' has already distributed (\d+) slot\(s\)\. Minimum nbr_max = (-?\d+) \(cannot reduce to (-?\d+)\)\.?$/i,
    translate: (m) =>
      `« ${m[1]} » a déjà distribué ${m[2]} place(s). ` +
      `Le quota minimum est de ${m[3]} (impossible de le réduire à ${m[4]}).`,
  },

  // ── Droits d'accès (catalog / rights) ────────────────────────────────
  {
    regex: /^Catalog entry (.+?) not found\.?$/i,
    translate: (m) => `L'entrée de catalogue « ${m[1]} » est introuvable.`,
  },
  {
    regex: /^You do not have the following right\(s\) to grant:\s*catalog ids=(.+)$/i,
    translate: (m) => `Vous ne disposez pas des droits suivants pour les accorder : ${m[1].trim()}.`,
  },
  {
    regex: /^User (.+?) is not in your hierarchy\.?$/i,
    translate: (m) => `L'utilisateur « ${m[1]} » ne fait pas partie de votre hiérarchie.`,
  },
  {
    regex: /^Cannot grant rights that none of the supervisors have:\s*catalog ids=(.+)$/i,
    translate: (m) => `Impossible d'accorder des droits qu'aucun superviseur ne possède : ${m[1].trim()}.`,
  },
  {
    regex: /^date_start \((.+?)\) is earlier than every supervisor's date_start for historic_fire_service \(right_id=(.+?)\)\.?$/i,
    translate: (m) =>
      `La date de début (${m[1]}) est antérieure à celle de tous les superviseurs ` +
      `pour l'historique des feux (droit ${m[2]}).`,
  },
  {
    regex: /^date_end \((.+?)\) exceeds every supervisor's date_end for historic_fire_service \(right_id=(.+?)\)\.?$/i,
    translate: (m) =>
      `La date de fin (${m[1]}) dépasse celle de tous les superviseurs ` +
      `pour l'historique des feux (droit ${m[2]}).`,
  },
  {
    regex: /^interval_min_j \((.+?)\) is lower than every supervisor's interval_min_j for weather \(right_id=(.+?)\)\.?$/i,
    translate: (m) =>
      `L'intervalle minimum (${m[1]} j) est inférieur à celui de tous les superviseurs ` +
      `pour la météo (droit ${m[2]}).`,
  },
  {
    regex: /^interval_max_j \((.+?)\) exceeds every supervisor's interval_max_j for weather \(right_id=(.+?)\)\.?$/i,
    translate: (m) =>
      `L'intervalle maximum (${m[1]} j) dépasse celui de tous les superviseurs ` +
      `pour la météo (droit ${m[2]}).`,
  },

  // ── Générique ─────────────────────────────────────────────────────────
  {
    regex: /^Database integrity error:/i,
    translate: () => 'Une erreur technique est survenue. Veuillez réessayer ou contacter le support.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// → message FR, ou null si le detail n'est pas reconnu
// ─────────────────────────────────────────────────────────────────────────────

export const translateSupervisionError = (detail) => {
  if (typeof detail !== 'string' || !detail) return null;

  // Cas structuré : suppression bloquée par supervision active
  const parsed = parseSupervisionError(detail);
  if (parsed) {
    const { owner, count, role, names } = parsed;
    const [singulier, pluriel] = ROLE_LABELS[role] || ROLE_LABELS.user;
    const label  = count > 1 ? pluriel : singulier;
    const liste  = names.length ? ` : ${names.join(', ')}` : '';
    const cible  = count > 1 ? `les ${pluriel}` : `l'${singulier}`;

    return (
      `Suppression impossible : « ${owner} » supervise encore ${count} ${label}${liste}. ` +
      `Veuillez d'abord réassigner ou retirer ${cible} concerné${count > 1 ? 's' : ''}.`
    );
  }

  // Tous les autres cas
  for (const { regex, translate } of ERROR_PATTERNS) {
    const match = detail.match(regex);
    if (match) return translate(match);
  }

  return null; // pas de traduction connue → fallback géré par l'appelant (detail brut)
};

export default { extractErrorDetail, parseSupervisionError, translateSupervisionError };